<template>
  <div class="cash-operation-page">
    <block-buttons
      title="Операции по кассе"
      description="Наличные, история операций и остаток в кассе"
      :actions="toolbarActions"
    >
      <template #icon>
        <Wallet :size="22" />
      </template>
    </block-buttons>

    <dl
      v-if="isVisibleInfo"
      class="cash-operation-page__summary"
    >
      <div>
        <dt>Остаток наличных</dt>
        <dd>{{ priceToFormat(lastBalance) }}</dd>
      </div>

      <div>
        <dt>Операций по выбранному типу</dt>
        <dd>{{ filteredGetData.length }}</dd>
      </div>

      <div>
        <dt>Последняя операция</dt>
        <dd>{{ lastOperationDate }}</dd>
      </div>

      <div>
        <dt>Рабочая смена</dt>
        <dd>{{ getOpenedWorkingShift ? 'Открыта' : 'Закрыта' }}</dd>
      </div>
    </dl>

    <p class="cash-operation-page__notice">
      Здесь отображаются операции с наличными. Остаток кассы не зависит от выбранного фильтра.

      <template v-if="!loading && !error && !getOpenedWorkingShift">
        Для создания операции
        <router-link to="/working-shift">откройте рабочую смену</router-link>.
      </template>
    </p>

    <data-table
      :rows="filteredGetData"
      :columns="tableColumns"
      :loading="loading"
      :error="error"
      :search-keys="['description', 'userName', 'operationType', 'productListName', 'amount', 'createdAtFormatted']"
      title="История кассовых операций"
      description="Суммы, сотрудники и назначение операций"
      search-placeholder="Описание, сотрудник или товар"
      empty-text="Кассовых операций с выбранным типом нет"
      @retry="loadDataWrapper"
    >
      <template #header>
        <span class="cash-operation-page__badge">Операций: {{ filteredGetData.length }}</span>
      </template>

      <template #filters>
        <label class="cash-operation-page__filter">
          Тип операции

          <select v-model="selectedCashOperationType">
            <option value="">Все типы</option>

            <option
              v-for="type in cashOperationTypes"
              :key="type.code"
              :value="type.code"
            >
              {{ type.name }}
            </option>
          </select>
        </label>
      </template>

      <template #cell-operationTypeName="{ row }">
        <span class="cash-operation-page__badge">{{ row.operationTypeName }}</span>
      </template>

      <template #cell-amount="{ row }">
        <strong>{{ priceToFormat(row.amount) }}</strong>
      </template>

      <template #cell-createdAt="{ row }">{{ row.createdAtFormatted }}</template>

      <template #cell-description="{ row }">
        <strong>{{ row.comment || 'Без описания' }}</strong>
        <small>Операция № {{ row.id }}</small>
      </template>

      <template #cell-actions="{ row }">
        <select
          :disabled="loading"
          :aria-label="`Действия с операцией ${row.id}`"
          @change="selectAction($event, row)"
        >
          <option value="">Действия</option>

          <option
            v-for="action in rowActions"
            :key="action.id"
            :value="action.id"
          >
            {{ action.label }}
          </option>
        </select>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Wallet, Plus, RefreshCw } from 'lucide-vue'
import BlockButtons from '@/components/blockButtons'
import DataTable from '@/components/dataTable'
import permissions from '@/mixins/permissions'
import { priceToFormat, formatDate } from '@/helper'

export default {
  name: 'CashOperationV2',
  components: { Wallet, BlockButtons, DataTable },
  mixins: [permissions],
  data () {
    return {
      loading: false,
      error: '',
      selectedCashOperationType: '',
      refreshAfterModal: false,
      lastBalance: null
    }
  },
  computed: {
    ...mapGetters('cashOperation', ['getData']),
    ...mapGetters('workingShift', ['getOpenedWorkingShift']),
    ...mapGetters('settings', ['getTableColumns', 'getDefaultValues']),
    ...mapGetters('product', ['getDataObject']),
    ...mapGetters('config', ['getIsOffline']),
    isVisibleInfo () {
      return !this.loading || !this.error
    },
    cashOperationTypes () {
      return this.getDefaultValues?.['cash-operation']
    },
    filteredGetData () {
      return this.selectedCashOperationType === ''
        ? this.getData
        : this.getData.filter(row => row.operationType === this.selectedCashOperationType)
    },
    lastOperationDate () {
      if (!this.getData.length) {
        return 0
      }

      return formatDate(this.getData.at(0).createdAt)
    },
    toolbarActions () {
      const actions = [
        {
          id: 'refresh',
          label: 'Обновить',
          icon: RefreshCw,
          disabled: this.loading,
          handler: this.loadDataWrapper
        }
      ]

      if (this.isAllowedOperation('create', 'cashOperation')) {
        actions.push({
          id: 'create',
          label: 'Создать кассовую операцию',
          icon: Plus,
          primary: true,
          disabled: this.loading || !this.getOpenedWorkingShift,
          handler: () => {
            this.openModal({
              name: 'create-base',
              propsData: {
                name: 'cash-operation',
                moduleAction: 'cashOperation',
                type: 'create',
                title: 'Создание кассовой операции',
                text: 'Укажите данные для кассовой операции',
                textCancelBtn: 'Закрыть',
                textConfirmBtn: 'Создать кассовую операцию',
                onClose: () => {
                  this.$bus.emit('update-cash-summary')
                }
              }
            })
          }
        })
      }

      return actions
    },
    tableColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['cash-operation'] || [])
      ]
    },
    rowActions () {
      return [
        {
          id: 'info',
          label: 'Информация',
          visible: true
        },
        {
          id: 'remove',
          label: 'Удалить',
          visible: this.isAllowedOperation('remove', 'cashOperation')
        }
      ].filter(({ visible }) => visible)
    }
  },
  mounted () {
    this.loadDataWrapper()
  },
  methods: {
    ...mapActions('cashOperation', ['loadData', 'loadLastBalance', 'remove']),
    ...mapActions('product', { loadProducts: 'loadData' }),
    ...mapActions('modal', ['openModal', 'closeModal']),
    priceToFormat,
    async loadDataWrapper () {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      const { error: cashOperationError } = await this.loadData()
      const { data: latestBalanceData, error: latestBalanceError } = await this.loadLastBalance()

      this.loading = false
      this.$bus.emit('stop-loader')

      if (cashOperationError || latestBalanceError) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при загрузке!',
          text: 'Произошла ошибка при загрузке данных баланса!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.lastBalance = latestBalanceData.balance
    },
    selectAction (event, row) {
      const action = event.target.value

      event.target.value = ''

      if (!action) {
        return
      }

      this.openAction(action, row)
    },
    openAction (action, row) {
      switch (action) {
        case 'info':
          this.openModal({
            name: 'info-base',
            propsData: {
              name: 'cash-operation',
              moduleAction: 'cashOperation',
              type: 'info',
              title: `Информация - ${row.operationTypeName} ${row.createdAtFormatted}`,
              textCancelBtn: 'Закрыть',
              data: row
            }
          })

          break
        case 'remove':
          this.removeCashOperation(row)
      }
    },
    async removeCashOperation ({ id }) {
      const { error } = await this.remove(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при удалении!',
          text: 'Произошла ошибка при удалении кассовой операции!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.$notify({
        type: 'success',
        title: 'Кассовая операция успешно удалена!',
        text: '',
        clean: false,
        duration: 3000
      })
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
